// src/pages/Catalog.js
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchProductsPage,
  fetchProductsAllCached,
  productUrl,
} from '../lib/products'
import { addToCart } from '../lib/cartStore'
import { Link, useSearchParams } from 'react-router-dom'
import { useUiFeedback } from '../context/UiFeedbackContext' // ✅ feedback global
import { formatSentenceCase } from "../utils/textFormat";

const PAGE_SIZE = 60 // ✅ nombre de produits par page

/* -------- Utils texte / recherche -------- */
function normalizeText(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // accents
    .toLowerCase()
}

/* Filtre local par catégorie */
function matchCatLocal(p, catId) {
  if (!catId) return true
  const pid =
    p?.Categorie?.Id ||
    p?.Categorie?.ID ||
    p?.Categorie?.id ||
    p?.Category?.Id ||
    p?.Category?.ID ||
    p?.Category?.id ||
    p?.categoryId ||
    ''
  return String(pid) === String(catId)
}

/* Recherche plus intelligente (inclut Name, name, Title, marque…) */
function matchesQuery(p, q) {
  const needle = normalizeText(q).trim()
  // 👉 si pas de recherche, on ne filtre pas
  if (!needle) return true

  const haystack = normalizeText(
    [
      p.title,
      p.Name,
      p.name,
      p.Title,
      p.description,
      p.Description,
      p.Brand,
      p.brand,
      p.Marque,
      p?.Categorie?.Label,
      p?.SubCategorie?.Label,
      p?.Category?.Label,
      p?.SubCategory?.Label,
      p.SKU,
      p.Upc,
      p.UPC,
    ]
      .filter(Boolean)
      .join(' ')
  )

  return haystack.includes(needle)
}
function SkeletonProductCard() {
  return (
    <div className="wm-product-card h-100 d-flex flex-column placeholder-glow">
      {/* Image */}
      <div className="wm-product-thumb mb-2">
        <span className="placeholder w-100 h-100" />
      </div>

      {/* Texte */}
      <div className="wm-product-body">
        <div className="wm-product-meta mb-1">
          <span className="placeholder" style={{ width: '60%', height: 10 }} />
        </div>
        <div className="mb-1">
          <span className="placeholder" style={{ width: '90%', height: 12 }} />
        </div>
        <div className="mb-2">
          <span className="placeholder" style={{ width: '70%', height: 12 }} />
        </div>

        <div className="wm-product-price-row">
          <span
            className="placeholder"
            style={{ width: 80, height: 16 }}
          />
        </div>
      </div>

      {/* Bouton */}
      <div className="wm-product-cta-row mt-2 d-flex">
        <span
          className="placeholder rounded-pill w-100"
          style={{ height: 30 }}
        />
      </div>
    </div>
  )
}
function EmptyState({
  q,
  catId,
  subId,
  onClearSearch,
  onClearFilters,
  onShowAll,
}) {
  return (
    <div className="text-center p-4 p-md-5 border rounded-4" style={{ borderColor: "#22304d" }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>🔎</div>

      <h5 className="mb-2">Aucun produit trouvé</h5>

      <p className="text-muted mb-3">
        {q?.trim()
          ? <>Aucun résultat pour <b>“{q}”</b>.</>
          : "Il n'y a pas encore de produits disponibles pour ce filtre."}
        {(catId || subId) && (
          <> Essayez de retirer les filtres.</>
        )}
      </p>

      <div className="d-flex flex-wrap justify-content-center gap-2">
        {q?.trim() && (
          <button className="btn btn-outline-light" onClick={onClearSearch}>
            Effacer la recherche
          </button>
        )}

        {(catId || subId) && (
          <button className="btn btn-outline-light" onClick={onClearFilters}>
            Retirer les filtres
          </button>
        )}

        <button className="btn btn-primary" onClick={onShowAll}>
          Voir tous les produits
        </button>
      </div>

      <div className="small text-muted mt-3">
        Astuce : essaye un mot plus court (ex: “shampoo” au lieu de “shampooing ultra hydratant”).
      </div>
    </div>
  );
}
function ProductCard({ p, qty = 0, onChangeQty }) {
  const price = Number(p.price || 0)

  return (
    <div className="wm-product-card h-100 d-flex flex-column">
      <Link
        to={productUrl(p)}
        className="wm-product-main-link text-decoration-none flex-grow-1"
      >
        <div className="wm-product-thumb">
          <img
            src={
              p.imageUrl ||
              'https://via.placeholder.com/640x480?text=Image+indisponible'
            }
            alt={p.title || 'Produit'}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src =
                'https://via.placeholder.com/640x480?text=Image+indisponible'
            }}
          />
        </div>

        <div className="wm-product-body">
          {p?.Categorie?.Label && p?.SubCategorie?.Label && (
            <div className="wm-product-meta">
              {p.Categorie.Label} / {p.SubCategorie.Label}
            </div>
          )}

          <div className="wm-product-title line-clamp-2">
            {formatSentenceCase(p.title)}
          </div>

          <div className="wm-product-price-row">
            <span className="wm-price-main">{price.toLocaleString()} XOF</span>
          </div>
        </div>
      </Link>

      <div className="wm-product-cta-row mt-2 d-flex">
        {qty === 0 ? (
          <button
            className="btn btn-primary btn-sm wm-btn-add"
            onClick={() => onChangeQty(p, +1)}
          >
            + Ajouter
          </button>
        ) : (
          <div className="qty-pill d-flex align-items-center">
            <button
              type="button"
              className="qty-btn"
              onClick={() => onChangeQty(p, -1)}
            >
              −
            </button>
            <span className="qty-value">{qty}</span>
            <button
              type="button"
              className="qty-btn"
              onClick={() => onChangeQty(p, +1)}
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
export default function Catalog() {
  const { notifyAddToCart } = useUiFeedback() // ✅ hook de feedback

  const [params] = useSearchParams()
  const catId = params.get('cat') || ''
  const catLabel = params.get('label') || ''
  const subId = params.get('sub') || ''
  const subLabel = params.get('sublabel') || ''
  const urlQ = params.get('q') || '' // 👈 query envoyée depuis la Home
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  // texte saisi
  const [q, setQ] = useState(urlQ)
  // texte utilisé pour filtrer (debouncé)
  const [debouncedQ, setDebouncedQ] = useState(urlQ)

  const [items, setItems] = useState([]) // produits paginés (affichage normal)
  const [cursor, setCursor] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // index global pour la recherche
  const [allItems, setAllItems] = useState(null) // null = pas encore chargés
  const [loadingAll, setLoadingAll] = useState(false)

  // quantité par produit pour le bouton - qty +
  const [qtyById, setQtyById] = useState({})

  // pagination client
  const [page, setPage] = useState(1)

  /* 🔁 Si l’URL change (ex: /catalog?q=shampoo depuis la Home),
     on synchronise le champ de recherche et la valeur debouncée */
  useEffect(() => {
    const qFromUrl = params.get('q') || ''
    setQ(qFromUrl)
    setDebouncedQ(qFromUrl)
  }, [params])

  /* 0) Debounce du texte saisi (UX) */
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(q)
    }, 300) // 300 ms

    return () => clearTimeout(id)
  }, [q])

  const isSearching = debouncedQ.trim().length > 0
  const isTyping = q !== debouncedQ

  /* 0bis) Quand on change de recherche ou de filtres, on revient à la page 1 */
  useEffect(() => {
    setPage(1)
  }, [debouncedQ, catId, subId])

  /* 1) Première page (affichage normal, paginé) depuis Firestore */
  const fetchFirstPage = useCallback(async () => {
    setItems([])
    setCursor(null)
    setHasMore(true)
    setLoading(true)
    setError(null)
    try {
      const { items: first, nextCursor, hasMore: more } =
        await fetchProductsPage({
          pageSize: PAGE_SIZE,
          cursor: null,
          subId,
          catId,
        })
      setItems(first)
      setCursor(nextCursor)
      setHasMore(more)
    } catch (e) {
      setError(e?.message || 'Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }, [subId, catId])

  /* 2) Pagination Firestore (fallback tant que l’index global n’est pas chargé) */
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return
    setLoading(true)
    setError(null)
    try {
      const { items: newItems, nextCursor, hasMore: more } =
        await fetchProductsPage({
          pageSize: PAGE_SIZE,
          cursor,
          subId,
          catId,
        })
      setItems((prev) => [...prev, ...newItems])
      setCursor(nextCursor)
      setHasMore(more)
    } catch (e) {
      setError(e?.message || 'Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }, [loading, hasMore, cursor, subId, catId])

  /* 3) Au montage + quand cat/sub changent (pour l’affichage) */
  useEffect(() => {
    fetchFirstPage()
    setQtyById({}) // reset affichage des quantités quand on change de filtre
  }, [fetchFirstPage])

  /* 4) Charger l’index global UNE SEULE FOIS au montage
        → pagination fluide + recherche instantanée */
  useEffect(() => {
    if (allItems !== null || loadingAll) return

      ; (async () => {
        try {
          setLoadingAll(true)
          const rows = await fetchProductsAllCached()
          setAllItems(rows)
        } finally {
          setLoadingAll(false)
        }
      })()
  }, [allItems, loadingAll])

  /* 5) Pipeline recherche + filtre + pagination client */
  const { results, totalCount, totalPages, currentPage } = useMemo(() => {
    // Dès que allItems est dispo, on l'utilise pour la recherche & pagination
    const base = allItems || items

    const subset = base.filter(
      (p) => matchesQuery(p, debouncedQ) && matchCatLocal(p, catId)
    )

    const totalPages = Math.max(1, Math.ceil(subset.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages)

    const start = (safePage - 1) * PAGE_SIZE
    const end = start + PAGE_SIZE
    const pageItems = subset.slice(start, end)

    return {
      results: pageItems,
      totalCount: subset.length,
      totalPages,
      currentPage: safePage,
    }
  }, [items, allItems, debouncedQ, catId, page])

  // ✅ remonter en haut à chaque changement de page
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth', // ou 'auto' si tu veux sans animation
    })
  }, [currentPage])

  const hasIndex = Boolean(allItems)

  // Tant que l’index global n’est pas chargé, on garde le bouton "Charger plus"
  const showLoadMore =
    !hasIndex && hasMore && !isSearching && !loadingAll

  /* 6) Gestion du bouton Ajouter / - qty + avec toast */
  const changeQty = (p, delta) => {
    setQtyById((prev) => {
      const current = prev[p.id] || 0
      const next = Math.max(current + delta, 0)
      const copy = { ...prev }

      if (next === 0) {
        delete copy[p.id]
      } else {
        copy[p.id] = next
      }

      // ✅ Feedback uniquement quand on ajoute (delta > 0)
      if (delta > 0 && next > current && notifyAddToCart) {
        notifyAddToCart({
          title: p.title || p.Name || 'Produit',
          qty: next,
          price: Number(p.price || 0),
        })
      }

      return copy
    })

    if (delta !== 0) {
      addToCart({
        pid: p.id,
        vendorId: p.vendorId || p.VendorId || 'goshop',
        qty: delta,
      })
    }
  }
  // useEffect(() => {
  //   if (totalCount !== 0) return;

  //   (async () => {
  //     try {
  //       setLoadingSug(true);
  //       const { items: sug } = await fetchProductsPage({ pageSize: 12, cursor: null, subId: "", catId: "" });
  //       setSuggestions(sug || []);
  //     } finally {
  //       setLoadingSug(false);
  //     }
  //   })();
  // }, [totalCount]);
  useEffect(() => {
    if (totalCount !== 0) return;
    if (loadingSug) return;
    if (suggestions?.length) return;

    (async () => {
      try {
        setLoadingSug(true);
        const { items: sug } = await fetchProductsPage({
          pageSize: 12,
          cursor: null,
          subId: "",
          catId: "",
        });
        setSuggestions(sug || []);
      } finally {
        setLoadingSug(false);
      }
    })();
  }, [totalCount]); // OK
  return (
    <div>
      {/* Barre recherche + résumé */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="input-group" style={{ maxWidth: 480 }}>
          <span className="input-group-text">
            <i className="bi bi-search" />
          </span>
          <input
            className="form-control"
            placeholder="Rechercher un produit, une marque, une catégorie..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setQ('')}
            >
              Effacer
            </button>
          )}
        </div>

        <div className="small text-muted ms-auto">
          {isTyping
            ? 'Recherche en cours…'
            : loading && !items.length
              ? 'Chargement des produits…'
              : `${totalCount} produit(s) trouvé(s)`}
        </div>
      </div>

      {/* Badges filtres */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {catId && (
          <span
            className="badge rounded-pill px-3 py-2"
            style={{
              background: '#101a33',
              border: '1px solid #22304d',
              color: '#e5e7eb',
            }}
          >
            Catégorie : {catLabel || catId}
          </span>
        )}
        {subId && (
          <span
            className="badge rounded-pill px-3 py-2"
            style={{
              background: '#101a33',
              border: '1px solid #22304d',
              color: '#e5e7eb',
            }}
          >
            Sous-catégorie : {subLabel || subId}
          </span>
        )}
        {loadingAll && (
          <span className="badge bg-light text-dark">
            Index des produits en cours de chargement…
          </span>
        )}
      </div>

      {error && <div className="alert alert-danger">Erreur : {error}</div>}

      {/* Aucun résultat */}
      {/* ✅ Loading initial : skeletons */}
      {(loading && !items.length) || (isSearching && !hasIndex && loadingAll) ? (
        <div className="mb-4">
          <div className="small text-muted mb-2">
            {isSearching ? "Recherche en cours…" : "Chargement des produits…"}
          </div>
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div className="col" key={idx}>
                <SkeletonProductCard />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ✅ EmptyState + Suggestions (quand c’est vraiment vide) */}
      {!loading && totalCount === 0 && (
        <>
          <EmptyState
            q={debouncedQ}
            catId={catId}
            subId={subId}
            onClearSearch={() => setQ("")}
            onClearFilters={() => (window.location.href = "/catalog")}
            onShowAll={() => (window.location.href = "/catalog")}
          />

          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Produits à découvrir</h6>
              {loadingSug && <span className="small text-muted">Chargement…</span>}
            </div>

            <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
              {(suggestions || []).slice(0, 8).map((p) => (
                <div className="col" key={p.id}>
                  <ProductCard
                    p={p}
                    qty={qtyById[p.id] || 0}
                    onChangeQty={changeQty}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}


      {/* Grille produits */}
      {/* Grille produits */}
      {totalCount > 0 && (
        <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
          {results.map((p) => (
            <div className="col" key={p.id}>
              <ProductCard
                p={p}
                qty={qtyById[p.id] || 0}
                onChangeQty={changeQty}
              />
            </div>
          ))}
        </div>
      )}

      {/* Pagination Firestore (fallback tant qu’on n’a pas allItems) */}
      {!hasIndex && (
        <div className="d-flex justify-content-center my-4">
          {showLoadMore ? (
            <button
              className="btn btn-soft"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? 'Chargement…' : 'Charger plus'}
            </button>
          ) : (
            !hasMore &&
            items.length > 0 &&
            !isSearching && (
              <div className="text-muted small">Fin de liste</div>
            )
          )}
        </div>
      )}

      {/* ✅ Pagination client (quand allItems est chargé) */}
      {hasIndex && totalCount > 0 && (
        <div className="d-flex justify-content-center align-items-center gap-3 my-4">
          <button
            type="button"
            className="btn btn-outline-light btn-sm"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Page précédente
          </button>

          <span className="small text-muted">
            Page {currentPage} / {totalPages}
          </span>

          <button
            type="button"
            className="btn btn-outline-light btn-sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Page suivante →
          </button>
        </div>
      )}
    </div>
  )
}
